// UTILS
import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import Bugsnag from "@bugsnag/js"
import { getAudio } from "@/api/s3"
import { initExtra, factoryTest, factoryItem, factoryStart } from "./brain.data"
import { useBrainStore } from "@/features/brain/stores/brain.store"

// TYPES
import type { Ref } from "vue"
import type { Extra } from "./brain.types"

// API
import {
  getAssessment,
  postAssessment,
  patchAssessment,
  deleteAssessment,
  getBrainNextItem,
} from "@/api/viktor/assessment/assessment"

// STORES
import { useI18nStore } from "@/stores/i18n"
import { useBrainTutoStore } from "./brainTuto.store"
import { useBrainLayoutStore } from "./brainLayout.store"
import { useUserStore } from "@/stores/user"
import { getVersionFromLang } from "../helpers/getVersionFromLang"

export const useAnswersStore = defineStore("brainAnswersStore", () => {
  const { currentLocale, showLang } = storeToRefs(useI18nStore())
  const { isTutorial, currentTutoStep } = storeToRefs(useBrainTutoStore())
  const { showSkipCTA } = useBrainLayoutStore()
  const userStore = useUserStore()
  const { isFromInt } = storeToRefs(useUserStore())

  // Ref
  const brainValues: Ref<null | any> = ref(null)
  const totalAnswers: Ref<number> = ref(0)
  const extra: Ref<Extra> = ref(initExtra)

  /**
   * Submit the current step to the brain machine
   */
  function submitStep() {
    useBrainStore().sendAnswer()
  }

  /**
   * Get the brain assessment
   */
  async function getBrain() {
    let data = await getAssessment("brain")
    const brainAssessment = userStore.assessmentSummary.assessments.brain

    if (brainAssessment?.status === "finish" && !brainAssessment.not_passed) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) data.status = "thank-you"
      else window.location.reload()
    } else if (data.status === "inprogress" || !!brainAssessment?.not_passed) {
      // if test was created_at more than 24h, init a new brain to restart the test
      if (await checkIfTestIsExpired(data.created_at)) {
        await deleteAssessment("brain")
        await initBrain(false, data.locale_id)
        data = await getAssessment("brain")
        resetBrainTest() // reset brain PIXI to get tutorial
      } else {
        isTutorial.value = false
        // check if object data.data exists
        if (Object.keys(data?.data).length > 0 && data?.data?.answers) {
          totalAnswers.value = Object.keys(data?.data?.answers).length
        }
      }
    }

    brainValues.value = data
    extra.value = brainValues.value.data?.extra ?? initExtra

    return data
  }

  /**
   * Fetch the audios for the brain assessment
   */
  function fetchAudios() {
    getAudio({ test: "brain" })
  }

  /**
   * Check if the test is expired
   * @param timestamp - The timestamp of the test
   * @returns True if the test is expired, false otherwise
   */
  async function checkIfTestIsExpired(timestamp: number) {
    const now = new Date().getTime() / 1000
    const diffHours = (now - timestamp) / (60 * 60)
    if (diffHours > 23) {
      return true
    } else {
      return false
    }
  }

  /**
   * Initialize the brain assessment
   * @param restarting - Whether the test is being restarted
   * @param lang - The language ID
   */
  async function initBrain(restarting?: boolean | null, lang?: number | null) {
    if (restarting) extra.value.isRestarting = true
    const payload = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ? lang : currentLocale.value.id,
      data: restarting ? { extra: { isRestarting: true } } : {},
    }

    try {
      await postAssessment("brain", payload)
    } catch (error) {
      console.error(error, payload)
      throw error
    }
  }

  /**
   * Start the brain assessment
   */
  async function startTest() {
    showLang.value = false

    if (totalAnswers.value < 1) {
      const payload = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: currentLocale.value.id,
        start: true,
      }
      const item = await patchAssessment("brain", payload)

      MixpanelInstance.track("brain_start", {
        payload: payload,
      })

      // push le first item
      useBrainStore().addItem(item)
    } else {
      // push le next item
      let nextQuestion
      try {
        nextQuestion = await getBrainNextItem()
      } catch (error: any) {
        // A 500 on next-items means the test session has expired on the science side (24h TTL):
        // the test can no longer be resumed, so delete it, init a new one and refresh (same as the 23h check)
        if (error?.response?.status === 500 && isFromInt.value === false) {
          await resetExpiredBrain(error)
          return
        }
        throw error
      }
      useBrainStore().addItem(nextQuestion)
    }
  }

  /**
   * Delete the expired brain, init a new one and refresh to restart from the tutorial
   * @param error - The next-items error that revealed the expiration
   */
  async function resetExpiredBrain(error: unknown) {
    Bugsnag.notify(error as Error, (event) => {
      event.context = "BrainAnswersStore.resetExpiredBrain"
      event.severity = "info"
      event.unhandled = false
    })

    await deleteAssessment("brain")
    await initBrain(false, brainValues.value?.locale_id)
    window.location.reload()
  }

  /**
   * Patch the brain assessment
   * @param data - The data to patch
   * @returns The patched assessment
   */
  async function patchTest(data) {
    const payload = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: currentLocale.value.id,
      data: { ...data },
    }
    try {
      return await patchAssessment("brain", payload)
    } catch (error) {
      console.error(error, payload)
      throw error
    }
  }

  /**
   * Show the breath screen
   */
  async function showBreath() {
    totalAnswers.value++
  }

  /**
   * Get the elapsed time for the brain assessment
   * @returns The elapsed time in seconds
   */
  async function getElapsedTime() {
    let time = 0
    const data = await getAssessment("brain")
    const answers = data.data.answers
    const arrayAnswers = Object.keys(answers)
    arrayAnswers.map((item) => (time += answers[item].time_initial))
    return Math.round(time)
  }

  /**
   * Reset the brain test
   */
  async function resetBrainTest() {
    isTutorial.value = true
    currentTutoStep.value = 0
    totalAnswers.value = 0
    showSkipCTA()
  }

  /**
   * Submit the final results for the brain assessment
   * @returns The submitted assessment
   */
  async function submitFinalResults() {
    extra.value.elapsed_time = await getElapsedTime()
    extra.value.finished = true
    // TODO: remove when backend no longer requires these fields
    extra.value.native_language = true
    extra.value.wholesome_conditions = true

    const payload = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: currentLocale.value.id,
      data: {
        extra: extra.value,
      },
    }
    MixpanelInstance.track("brain_finish", {
      payload: payload,
    })
    return await patchAssessment("brain", payload)
  }

  /**
   * Patch the factory test for the brain assessment
   * @param isRestarting - Whether the test is being restarted
   */
  async function patchFactoryTest(isRestarting: boolean) {
    try {
      await deleteAssessment("brain")
    } catch {
      return
    }

    await initBrain(isRestarting)
    await patchAssessment("brain", factoryStart)

    for (const item of factoryItem) {
      // Patch responses
      const payload = factoryTest(item)
      await patchAssessment("brain", payload)
    }
    window.location.reload()
  }

  /**
   * Reset the user data for the brain assessment
   */
  async function resetUserDataLight() {
    try {
      await deleteAssessment("brain")
    } catch {
      return
    }
    await resetBrainTest()
    window.location.reload()
  }

  return {
    brainValues,
    extra,
    totalAnswers,
    initBrain,
    getBrain,
    fetchAudios,
    startTest,
    submitStep,
    patchTest,
    showBreath,
    submitFinalResults,
    patchFactoryTest,
    resetUserDataLight,
  }
})
